/**
 * Shared constants + helpers for the PPR module.
 */

import type { PprStatus, PprReviewType } from '@/lib/apiClient';

/** The stages a review moves THROUGH — what the Stage dropdown offers.
 *  Cancelled is deliberately absent: it has its own control, because setting it
 *  needs a reason and re-arms the project's closeout alert, and a plain dropdown
 *  option would be a one-click accidental cancel on a live review. */
export const PPR_PIPELINE_STATUSES: readonly PprStatus[] = [
  'Initiating',
  'Survey Collection',
  'Awaiting Financials',
  'Financials Completed by Accounting',
  'Report Prep',
  'Complete',
];

/** Every status a job can be in, pipeline plus Cancelled. Used for the board's
 *  columns and status lookups — NOT for offering a stage to move to. */
export const PPR_STATUSES: readonly PprStatus[] = [
  ...PPR_PIPELINE_STATUSES,
  'Cancelled',
];

/** Terminal statuses. Each is hidden from the board behind its own checkbox, and
 *  excluded from the "Active PPRs" count. */
export const PPR_TERMINAL_STATUSES: readonly PprStatus[] = ['Complete', 'Cancelled'];

export const STATUS_COLORS: Record<PprStatus, { bg: string; text: string }> = {
  'Initiating':          { bg: '#E1EEF7', text: '#0D4C7C' },
  'Survey Collection':   { bg: '#FEF3C7', text: '#92400E' },
  'Awaiting Financials': { bg: '#FBE4DF', text: '#8F2414' },
  // Quality blue: accounting are done and the ball is with the coordinator —
  // deliberately not green, which reads as "finished".
  'Financials Completed by Accounting': { bg: '#E1EEF7', text: '#0D4C7C' },
  'Report Prep':         { bg: '#E6F2EB', text: '#015A28' },
  'Complete':            { bg: '#EDEDEC', text: '#454543' },
  // Muted grey with a red cast: terminal like Complete, but stopped rather than
  // finished. Not the full danger red — a cancelled review is a tidy-up, not an
  // alarm the coordinator needs to act on.
  'Cancelled':           { bg: '#F0EAE8', text: '#7A5C55' },
};

export const REVIEW_TYPE_COLORS: Record<PprReviewType, { bg: string; text: string }> = {
  'Full Review':    { bg: '#E6F2EB', text: '#015A28' },
  'Financial Only': { bg: '#FEF3C7', text: '#92400E' },
};

/** Brief: < $250K → Financial Only, ≥ $250K → Full Review (coordinator may override). */
export function defaultReviewType(jobValue: number): PprReviewType {
  return jobValue >= 250_000 ? 'Full Review' : 'Financial Only';
}

export function fmtUsd(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function fmtUsdPrecise(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function fmtPct(decimal: number | null | undefined): string {
  if (decimal === null || decimal === undefined) return '—';
  return (decimal * 100).toFixed(2) + '%';
}

// Delegates to lib/dates: close dates are calendar dates — naive new Date()
// parsing shifted them back a day in Eastern time.
export { fmtDate } from '@/lib/dates';

/** Days since the given ISO timestamp, in whole days. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}
